import { Dispatch, SetStateAction, useContext } from "react";
import TransitionWorkflowPageSideBar from "./transitions/TransitionWorkflowPageSideBar";
import WorkflowNoSource from "./WorkflowNoSource";
import { useModal, WorkflowContext } from "../contexts";
import WorkflowSourceFieldTable from "./WorkflowSourceFieldTable";
import ToggleButton from "./ToggleButton";
import ProLabel from "./ProLabel";
import useUser from "../hooks/useUser";
import WorkflowPaymentConfig from "./WorkflowPaymentConfig";
import { DocumentTextIcon, PaperClipIcon } from "@heroicons/react/outline";
import WorkflowSelectModal from "./WorkflowSelectModal";
import { Email } from "../interfaces";
import PayWallModalPro from "./PayWallModalPro";
import { putData } from "../backend";
import { useTranslation } from "react-i18next";

type Props = {
  email: Email;
  setEmail: Dispatch<SetStateAction<Email | undefined>>;
  toggleAttachmentMenu: () => void;
}

function WorkflowEmailSidebar(props: Props) {
  const { email, setEmail, toggleAttachmentMenu } = props;
  const { t } = useTranslation();
  const workflowContext = useContext(WorkflowContext)
  const { workflow, mutateWorkflow } = workflowContext;
  const { user } = useUser();
  const { openModal } = useModal();

  async function updateEnableTracking(enableTracking: boolean) {
    if (!user?.subscription?.featureSignatures) // TODO: Change this to its own feature flag
      return openModal(<PayWallModalPro />);

    let updatedEmail = { ...email!, enableTracking };
    setEmail(updatedEmail);

    updatedEmail = await putData(`/workflows/emails/${email.id}/`, updatedEmail);
    setEmail(updatedEmail);
  }

  async function handleToggleRemoveBranding(removeBranding: boolean) {
    if (!user?.subscription?.featureRemoveBranding)
      openModal(<PayWallModalPro reason="BRANDING" />);

    let updatedEmail = { ...email!, removeBranding };
    setEmail(updatedEmail);

    updatedEmail = await putData(`/workflows/emails/${email.id}/`, updatedEmail);
    setEmail(updatedEmail);
  }

  async function handleToggleEnableDraft(autoSend: boolean) {
    let updatedEmail = { ...email!, enableDraft: !autoSend };
    setEmail(updatedEmail);

    updatedEmail = await putData(`/workflows/emails/${email.id}/`, updatedEmail);
    setEmail(updatedEmail);

    mutateWorkflow();
  }

  return (
    <TransitionWorkflowPageSideBar>

      {!workflow.source &&
        <WorkflowNoSource />
      }

      {workflow.source && <>
        <WorkflowSourceFieldTable workflow={workflow} mutateWorkflow={mutateWorkflow} />

        <div className="flex flex-col gap-2">
          <div className='text-sm font-semibold font-gilroy text-gray-600'>
            {t("Email Settings")}
          </div>

          <div className='flex items-start gap-2'>
            <ToggleButton value={!email.enableDraft} onChange={handleToggleEnableDraft} />
            <div className="flex flex-col">
              <div className='text-sm font-semibold text-gray-600 flex-shrink'>
                {t('Auto-send emails')}
              </div>
              <div className='text-xs font-semibold text-gray-400 flex-shrink'>
                {t('When disabled a draft will be created to be manually sent instead.')}
              </div>
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <ToggleButton value={email.enableTracking} onChange={updateEnableTracking} />
            <div className='text-sm font-semibold text-gray-600 flex-shrink'>
              {t("Track when email is opened")}
            </div>
            <ProLabel />
          </div>

          <div className='flex items-center gap-2'>
            <ToggleButton value={!!user?.subscription?.featureRemoveBranding && email.removeBranding} onChange={handleToggleRemoveBranding} />
            <div className='text-sm font-semibold text-gray-600 flex-shrink'>
              {t('Remove "Sent via Portant"')}
            </div>
            <ProLabel />
          </div>
        </div>

        {workflow.documents.length > 0 &&
          <button className='text-sm btn btn-blue' onClick={toggleAttachmentMenu}>
            {t("Add Attachments")} <PaperClipIcon className='w-4 h-4 ml-2 text-white' />
          </button>

        }

        {workflow.documents.length === 0 && <>
          <hr />
          <div className="flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <div className="flex items-center font-gilroy font-semibold text-gray-600">
                <DocumentTextIcon className="w-6 h-6 mr-1" /> {t("Add document")}
              </div>
              <div className="font-semibold text-xs text-gray-400">
                {t("Add a templated document to be included with this email")}
              </div>
            </div>
            <button className="btn btn-white w-40 text-xs" onClick={() => openModal(<WorkflowSelectModal title={t("Add document")} subtitle={t("Add a templated document to be included with this email")} showDocuments {...workflowContext} />)}>
              {t("Add")}
            </button>
          </div>
        </>}

        <hr />
        <WorkflowPaymentConfig />

      </>}
    </TransitionWorkflowPageSideBar>
  );
}

export default WorkflowEmailSidebar;
